/**
 * Building a product: sections, the fields in them, and the rules that decide
 * when each one appears.
 *
 * Everything on this screen edits one draft version. A published version is
 * shown read-only with a single button that opens the next draft, because a
 * published version is what applications are stored against and must not move
 * under them.
 */
import { useMemo, useState } from "react";
import type { Condition } from "@dp/shared";
import { ApiError, api } from "../../lib/api.ts";
import { useAsync } from "../../lib/useAsync.ts";
import { useAuth } from "../../lib/auth.tsx";
import { DATA_TYPE_LABELS, type DataType, type DomainRow, type EntityRow, type FieldRow } from "../../lib/types.ts";
import { BackLink, Loading, PageHead, Problem, Tiles } from "../../components/Page.tsx";
import { FieldPicker } from "../../components/FieldPicker.tsx";
import { ConditionEditor, type ConditionField } from "../../components/ConditionEditor.tsx";

interface VersionSummary {
  id: string;
  version: number;
  status: "draft" | "published" | "retired";
  notes: string | null;
  publishedAt: string | null;
}

interface AttachedField {
  id: string;
  fieldKey: string;
  label: string;
  libraryLabel: string;
  dataType: string;
  active: boolean;
  sortOrder: number;
  required: boolean;
  readOnly: boolean;
  defaultValue: unknown;
  visibleWhen: Condition | null;
  overrides: { label?: string | null; helpText?: string | null; allowedCodes?: string[] | null } | null;
}

interface Section {
  id: string;
  key: string;
  label: string;
  description: string | null;
  entityKey: string;
  entityLabel: string;
  repeating: boolean;
  sortOrder: number;
  visibleWhen: Condition | null;
  fields: AttachedField[];
}

interface Definition {
  product: { id: string; code: string; name: string; lineOfBusiness: string; description: string | null };
  version: VersionSummary;
  sections: Section[];
}

export function ProductBuilder({ code }: { code: string }) {
  const overview = useAsync(
    () => api.get<{ product: Definition["product"]; versions: VersionSummary[] }>(`/api/products/${code}`),
    [code],
  );
  const [chosenVersion, setChosenVersion] = useState<string | null>(null);

  const versions = overview.data?.versions ?? [];
  const draft = versions.find((version) => version.status === "draft");
  const live = versions.find((version) => version.status === "published");
  const activeVersionId = chosenVersion ?? draft?.id ?? live?.id ?? versions[0]?.id ?? null;

  if (overview.loading && !overview.data) return <Loading what="the product" />;
  if (overview.error) return <Problem error={overview.error} />;
  if (!activeVersionId) return <Problem error={new Error("This product has no versions.")} />;

  return (
    <Builder
      key={activeVersionId}
      code={code}
      versionId={activeVersionId}
      versions={versions}
      onSwitchVersion={setChosenVersion}
      onVersionsChanged={(nextId) => { setChosenVersion(nextId ?? null); overview.reload(); }}
    />
  );
}

function Builder({
  code, versionId, versions, onSwitchVersion, onVersionsChanged,
}: {
  code: string;
  versionId: string;
  versions: VersionSummary[];
  onSwitchVersion: (id: string) => void;
  onVersionsChanged: (nextId?: string) => void;
}) {
  const { can } = useAuth();
  const definition = useAsync(() => api.get<Definition>(`/api/versions/${versionId}`), [versionId]);
  const library = useAsync(() => api.get<{ fields: FieldRow[] }>("/api/fields"), []);
  const entities = useAsync(() => api.get<{ entities: EntityRow[] }>("/api/entities"), []);
  const domains = useAsync(() => api.get<{ domains: DomainRow[] }>("/api/domains"), []);

  const [problem, setProblem] = useState<string | null>(null);
  const [addingSection, setAddingSection] = useState(false);
  const [pickingInto, setPickingInto] = useState<string | null>(null);
  const [openField, setOpenField] = useState<string | null>(null);

  const sections = definition.data?.sections ?? [];
  const product = definition.data?.product;
  const version = definition.data?.version;
  const editable = version?.status === "draft" && can("products", "edit");

  const attachedKeys = useMemo(
    () => new Set(sections.flatMap((section) => section.fields.map((field) => field.fieldKey))),
    [sections],
  );

  /** What a visibility rule may read: every field this product already asks. */
  const conditionFields: ConditionField[] = useMemo(() => {
    const byKey = new Map(library.data?.fields.map((field) => [field.key, field]) ?? []);
    const domainByKey = new Map(domains.data?.domains.map((domain) => [domain.key, domain]) ?? []);
    return sections.flatMap((section) =>
      section.fields.map((attached) => {
        const field = byKey.get(attached.fieldKey);
        const domain = field?.valueDomainKey ? domainByKey.get(field.valueDomainKey) : undefined;
        return {
          key: attached.fieldKey,
          label: `${section.label} · ${attached.label}`,
          dataType: (field?.dataType ?? "text") as DataType,
          options: domain?.members.filter((member) => member.active).map((member) => ({ code: member.code, label: member.label })),
        };
      }),
    );
  }, [sections, library.data, domains.data]);

  async function run(work: () => Promise<unknown>) {
    setProblem(null);
    try {
      await work();
      definition.reload();
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "That did not work.");
    }
  }

  async function publish() {
    setProblem(null);
    try {
      await api.post(`/api/versions/${versionId}/publish`, {});
      onVersionsChanged(versionId);
      definition.reload();
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "Could not publish this product.");
    }
  }

  async function openNextDraft() {
    setProblem(null);
    try {
      const result = await api.post<{ version: VersionSummary }>(`/api/products/${code}/draft`);
      onVersionsChanged(result.version.id);
    } catch (error) {
      setProblem(error instanceof ApiError ? error.message : "Could not open a draft.");
    }
  }

  function move(sectionIndex: number, fieldIndex: number | null, by: number) {
    const order = {
      sections: sections.map((section) => section.id),
      fields: Object.fromEntries(sections.map((section) => [section.id, section.fields.map((field) => field.id)])),
    };

    if (fieldIndex === null) {
      const target = sectionIndex + by;
      if (target < 0 || target >= order.sections.length) return;
      const [moved] = order.sections.splice(sectionIndex, 1);
      order.sections.splice(target, 0, moved!);
    } else {
      const section = sections[sectionIndex]!;
      const list = order.fields[section.id]!;
      const target = fieldIndex + by;
      if (target < 0 || target >= list.length) return;
      const [moved] = list.splice(fieldIndex, 1);
      list.splice(target, 0, moved!);
    }

    void run(() => api.post(`/api/versions/${versionId}/order`, order));
  }

  if (definition.loading && !definition.data) return <Loading what="the product" />;
  if (definition.error) return <Problem error={definition.error} />;

  const fieldCount = sections.reduce((total, section) => total + section.fields.length, 0);
  const requiredCount = sections.reduce((total, section) => total + section.fields.filter((field) => field.required).length, 0);
  const ruledCount = sections.reduce((total, section) => total + section.fields.filter((field) => field.visibleWhen).length, 0);

  return (
    <div className="stack">
      <BackLink to="/products">Products</BackLink>

      <PageHead
        title={product?.name ?? code}
        sub={<>
          <span className="key">{code}</span> · {product?.lineOfBusiness}
        </>}
        actions={
          <div className="row">
            <select
              className="control control-inline"
              value={versionId}
              onChange={(event) => onSwitchVersion(event.target.value)}
            >
              {versions.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  v{candidate.version} · {candidate.status}
                </option>
              ))}
            </select>
            {editable
              ? <button className="btn btn-primary" type="button" onClick={() => void publish()}>Publish v{version?.version}</button>
              : can("products", "edit") && <button className="btn btn-primary" type="button" onClick={() => void openNextDraft()}>Open a new draft</button>}
          </div>
        }
      />

      {problem && <div className="notice notice-bad">{problem}</div>}

      {version?.status === "published" && (
        <div className="notice notice-ok">
          <strong>Live.</strong> This version is frozen — applications are stored against it, so it never changes.
          Editing opens v{Math.max(...versions.map((candidate) => candidate.version)) + (versions.some((candidate) => candidate.status === "draft") ? 0 : 1)} as a draft.
        </div>
      )}
      {version?.status === "retired" && (
        <div className="notice notice-warn">
          <strong>Retired.</strong> Superseded by a later version. Applications that were started on it still use it.
        </div>
      )}

      <Tiles
        tiles={[
          { label: "Sections", value: sections.length, tone: "accent" },
          { label: "Questions asked", value: fieldCount, tone: "info" },
          { label: "Required", value: requiredCount, tone: "warn" },
          { label: "Conditional", value: ruledCount, tone: "muted" },
        ]}
      />

      {sections.length === 0 && (
        <div className="card">
          <div className="empty">
            <h3>Nothing asked yet</h3>
            <p className="small">A section collects one entity — the applicant, the locations. Add the first one.</p>
          </div>
        </div>
      )}

      {sections.map((section, sectionIndex) => (
        <div className="card" key={section.id}>
          <div className="card-head">
            <div className="stack-sm">
              <div className="row">
                <h2>{section.label}</h2>
                <span className="chip">{section.entityLabel}</span>
                {section.repeating && <span className="chip chip-info">a grid — many rows</span>}
              </div>
              {section.description && <p className="small muted">{section.description}</p>}
            </div>
            {editable && (
              <div className="row">
                <button className="btn btn-sm btn-ghost" type="button" aria-label="Move section up"
                  onClick={() => move(sectionIndex, null, -1)} disabled={sectionIndex === 0}>↑</button>
                <button className="btn btn-sm btn-ghost" type="button" aria-label="Move section down"
                  onClick={() => move(sectionIndex, null, 1)} disabled={sectionIndex === sections.length - 1}>↓</button>
                <button className="btn btn-sm" type="button" onClick={() => setPickingInto(section.id)}>Add a field</button>
                <button className="btn btn-sm btn-danger" type="button"
                  onClick={() => void run(() => api.del(`/api/sections/${section.id}`))}>Remove</button>
              </div>
            )}
          </div>

          {pickingInto === section.id && (
            <div className="card-body">
              <FieldPicker
                fields={library.data?.fields ?? []}
                entityKey={section.entityKey}
                alreadyUsed={attachedKeys}
                onCancel={() => setPickingInto(null)}
                onPick={(field) => {
                  setPickingInto(null);
                  void run(() => api.post(`/api/sections/${section.id}/fields`, { fieldKey: field.key }));
                }}
              />
            </div>
          )}

          {section.fields.length === 0 ? (
            <div className="empty small">No fields in this section yet.</div>
          ) : (
            <div className="attached">
              {section.fields.map((field, fieldIndex) => (
                <AttachedRow
                  key={field.id}
                  field={field}
                  editable={Boolean(editable)}
                  open={openField === field.id}
                  conditionFields={conditionFields.filter((candidate) => candidate.key !== field.fieldKey)}
                  onToggle={() => setOpenField(openField === field.id ? null : field.id)}
                  onMove={(by) => move(sectionIndex, fieldIndex, by)}
                  canMoveUp={fieldIndex > 0}
                  canMoveDown={fieldIndex < section.fields.length - 1}
                  onChange={(changes) => void run(() => api.patch(`/api/field-refs/${field.id}`, changes))}
                  onDetach={() => void run(() => api.del(`/api/field-refs/${field.id}`))}
                />
              ))}
            </div>
          )}
        </div>
      ))}

      {editable && (
        addingSection ? (
          <NewSection
            entities={entities.data?.entities ?? []}
            usedRepeating={new Set(sections.filter((section) => section.repeating).map((section) => section.entityKey))}
            onCancel={() => setAddingSection(false)}
            onCreate={(input) => {
              setAddingSection(false);
              void run(() => api.post(`/api/versions/${versionId}/sections`, input));
            }}
          />
        ) : (
          <button className="btn" type="button" onClick={() => setAddingSection(true)}>Add a section</button>
        )
      )}
    </div>
  );
}

function AttachedRow({
  field, editable, open, conditionFields, onToggle, onChange, onDetach, onMove, canMoveUp, canMoveDown,
}: {
  field: AttachedField;
  editable: boolean;
  open: boolean;
  conditionFields: ConditionField[];
  onToggle: () => void;
  onChange: (changes: Record<string, unknown>) => void;
  onDetach: () => void;
  onMove: (by: number) => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
}) {
  return (
    <div className={`attached-row${open ? " is-open" : ""}`}>
      <div className="attached-main">
        <div className="attached-label">
          <button type="button" className="attached-name" onClick={onToggle}>
            {field.label}
          </button>
          <div className="row small muted">
            <span className="key">{field.fieldKey}</span>
            <span className="chip">{DATA_TYPE_LABELS[field.dataType as DataType] ?? field.dataType}</span>
            {field.required && <span className="chip chip-warn">required</span>}
            {field.readOnly && <span className="chip">read only</span>}
            {field.visibleWhen && <span className="chip chip-info">conditional</span>}
            {!field.active && <span className="chip chip-bad">retired in the library</span>}
          </div>
        </div>

        {editable && (
          <div className="row">
            <label className="checkline small">
              <input type="checkbox" checked={field.required} onChange={(event) => onChange({ required: event.target.checked })} />
              Required
            </label>
            <button className="btn btn-sm btn-ghost" type="button" aria-label="Move up" onClick={() => onMove(-1)} disabled={!canMoveUp}>↑</button>
            <button className="btn btn-sm btn-ghost" type="button" aria-label="Move down" onClick={() => onMove(1)} disabled={!canMoveDown}>↓</button>
            <button className="btn btn-sm btn-ghost" type="button" onClick={onToggle}>{open ? "Done" : "Settings"}</button>
            <button className="btn btn-sm btn-ghost" type="button" aria-label="Remove" onClick={onDetach}>×</button>
          </div>
        )}
      </div>

      {open && (
        <div className="attached-detail stack">
          <div className="field-row">
            <div className="field">
              <label htmlFor={`label-${field.id}`}>Wording on this form</label>
              <input
                id={`label-${field.id}`} className="control" defaultValue={field.overrides?.label ?? ""}
                placeholder={field.libraryLabel} disabled={!editable}
                onBlur={(event) => onChange({ overrides: { ...field.overrides, label: event.target.value.trim() || null } })}
              />
              <span className="hint">Left blank it uses the library wording: “{field.libraryLabel}”.</span>
            </div>
            <div className="field">
              <label htmlFor={`help-${field.id}`}>Help text on this form</label>
              <input
                id={`help-${field.id}`} className="control" defaultValue={field.overrides?.helpText ?? ""} disabled={!editable}
                onBlur={(event) => onChange({ overrides: { ...field.overrides, helpText: event.target.value.trim() || null } })}
              />
            </div>
          </div>

          <label className="checkline">
            <input type="checkbox" checked={field.readOnly} disabled={!editable}
              onChange={(event) => onChange({ readOnly: event.target.checked })} />
            <span>Shown but not editable<div className="small muted">For values this product works out rather than asks.</div></span>
          </label>

          <div className="field">
            <label>When to show it</label>
            {editable ? (
              <ConditionEditor
                value={field.visibleWhen}
                fields={conditionFields}
                onChange={(condition) => onChange({ visibleWhen: condition })}
              />
            ) : (
              <span className="small muted">{field.visibleWhen ? "Conditional." : "Always shown."}</span>
            )}
            <span className="hint">
              A hidden field is never required — the form cannot demand an answer it did not ask for.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function NewSection({
  entities, usedRepeating, onCreate, onCancel,
}: {
  entities: EntityRow[];
  usedRepeating: Set<string>;
  onCreate: (input: { label: string; entityKey: string; description: string | null }) => void;
  onCancel: () => void;
}) {
  const available = entities.filter((entity) => !(entity.repeating && usedRepeating.has(entity.key)));
  const [label, setLabel] = useState("");
  const [entityKey, setEntityKey] = useState(available[0]?.key ?? "");
  const [description, setDescription] = useState("");

  const entity = entities.find((candidate) => candidate.key === entityKey);

  return (
    <form
      className="card"
      onSubmit={(event) => {
        event.preventDefault();
        onCreate({ label: label.trim(), entityKey, description: description.trim() || null });
      }}
    >
      <div className="card-head"><h2>New section</h2></div>
      <div className="card-body stack">
        <div className="field-row">
          <div className="field">
            <label htmlFor="section-label">Heading<span className="required-mark">*</span></label>
            <input id="section-label" className="control" value={label} required autoFocus
              placeholder="The applicant" onChange={(event) => setLabel(event.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="section-entity">Collects<span className="required-mark">*</span></label>
            <select id="section-entity" className="control" value={entityKey} onChange={(event) => setEntityKey(event.target.value)}>
              {available.map((candidate) => (
                <option key={candidate.key} value={candidate.key}>{candidate.label}{candidate.repeating ? " (many)" : ""}</option>
              ))}
            </select>
            <span className="hint">
              {entity?.repeating
                ? "This section becomes a grid, and answers are stored as a list."
                : "One set of answers per application."}
            </span>
          </div>
        </div>
        <div className="field">
          <label htmlFor="section-description">Description</label>
          <input id="section-description" className="control" value={description}
            placeholder="Shown under the heading on the form." onChange={(event) => setDescription(event.target.value)} />
        </div>
      </div>
      <div className="card-foot">
        <button type="button" className="btn" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={!entityKey}>Add section</button>
      </div>
    </form>
  );
}
